import React, { useEffect, useState } from 'react';
import { loadServiceProviders, providersForCategory, rememberProvider } from '../services/serviceProviders';

// Each category the seller's agent may mention, with the words that identify it in a pasted reply.
export const PROPERTY_INFO_FIELDS = [
  { key: 'electric', label: 'Electric provider', words: ['electric', 'electricity', 'power', 'fpl', 'lee county electric'] },
  { key: 'gas', label: 'Gas provider', words: ['gas'] },
  { key: 'water', label: 'Water and sewer provider', words: ['water', 'sewer'] },
  { key: 'trash', label: 'Trash and waste provider', words: ['trash', 'waste', 'garbage', 'recycling'] },
  { key: 'internet', label: 'Internet and cable provider', words: ['internet', 'cable', 'wifi', 'xfinity', 'spectrum', 'comcast', 'frontier'] },
  { key: 'pool', label: 'Pool service company', words: ['pool'] },
  { key: 'pest', label: 'Pest control company', words: ['pest'] },
  { key: 'lawn', label: 'Lawn or landscaping company', words: ['lawn', 'landscap'] },
  { key: 'alarm', label: 'Alarm or security company', words: ['alarm', 'security', 'adt'] },
];

const NOT_APPLICABLE = /\b(n\/a|na|not applicable|none|does not apply|doesn't apply|no pool|no gas)\b/i;

// Pulls a likely answer for each category from the pasted reply. Results are proposals only; the user confirms each one.
export const proposeFromReply = (text) => {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const proposals = {};
  PROPERTY_INFO_FIELDS.forEach((field) => {
    const hit = lines.find((line) => field.words.some((w) => line.toLowerCase().includes(w)));
    if (!hit) return;
    const afterColon = hit.includes(':') ? hit.split(':').slice(1).join(':').trim() : '';
    const afterDash = !afterColon && hit.includes(' - ') ? hit.split(' - ').slice(1).join(' - ').trim() : '';
    const value = (afterColon || afterDash || hit).replace(/^[-•*]\s*/, '').trim();
    proposals[field.key] = { value, notApplicable: NOT_APPLICABLE.test(value), confirmed: false };
  });
  return proposals;
};

const box = { width: '100%', padding: 10, border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };

export default function PropertyInfoSection({ value = {}, onChange }) {
  const [reply, setReply] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [saved, setSaved] = useState({});

  useEffect(() => {
    loadServiceProviders().then(setSaved);
  }, []);

  const setField = (key, patch) => {
    onChange({ ...value, [key]: { ...(value[key] || { value: '', notApplicable: false, confirmed: false }), ...patch } });
  };

  const findProviders = () => {
    const proposals = proposeFromReply(reply);
    onChange({ ...value, ...Object.fromEntries(Object.entries(proposals).map(([k, p]) => [k, { ...(value[k] || {}), ...p }])) });
    setReviewing(true);
  };

  return (
    <fieldset style={{ border: '1px solid #e5dcc8', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <legend style={{ fontWeight: 700, padding: '0 6px' }}>Property and Service Information</legend>
      <p style={{ margin: '0 0 8px', fontSize: 14 }}>Paste the seller's agent's reply below, then check each answer before it's saved.</p>
      <textarea style={{ ...box, minHeight: 120 }} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Paste the reply here" />
      <div style={{ margin: '8px 0 16px' }}>
        <button type="button" onClick={findProviders} disabled={!reply.trim()} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #b8a57a', background: '#fff', cursor: 'pointer' }}>
          Find providers in reply
        </button>
      </div>
      {reviewing && PROPERTY_INFO_FIELDS.map((field) => {
        const item = value[field.key] || { value: '', notApplicable: false, confirmed: false };
        return (
          <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <label style={{ fontSize: 14 }}>{field.label}</label>
            <div>
              <input list={`list-${field.key}`} style={box} value={item.value} onChange={(e) => setField(field.key, { value: e.target.value, notApplicable: NOT_APPLICABLE.test(e.target.value) })} placeholder="Not found in reply" />
              <datalist id={`list-${field.key}`}>
                {providersForCategory(saved, field.key).map((name) => <option key={name} value={name} />)}
              </datalist>
            </div>
            <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!item.confirmed} onChange={(e) => { setField(field.key, { confirmed: e.target.checked }); if (e.target.checked && item.value && !item.notApplicable) rememberProvider(field.key, item.value).then(() => loadServiceProviders().then(setSaved)); }} /> Confirmed
            </label>
          </div>
        );
      })}
      <p style={{ margin: 0, fontSize: 12, color: '#6b5f45' }}>Only confirmed answers go into the closing week email. Blank or "N/A" answers are left out.</p>
    </fieldset>
  );
}
